import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { TrendingUp, FileDown, Building2, Save, Upload, Lock, Sparkles, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LANGS } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const eur = (n) => `${(Number(n) || 0).toFixed(2)} €`;

function CompanyDialog() {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState({ name: "", address: "", city: "", phone: "", email: "", vat: "", logo: "" });
  const [saving, setSaving] = useState(false);

  const loadCompany = async () => {
    try { const { data } = await api.get("/profile/company"); setC((p) => ({ ...p, ...(data.company || {}) })); }
    catch { /* ignore */ }
  };
  useEffect(() => { if (open) loadCompany(); }, [open]);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Logo trop lourd (max 1,5 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => setC((p) => ({ ...p, logo: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile/company", c); toast.success("Entête enregistré"); setOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full border-slate-300" data-testid="open-company-btn">
          <Building2 className="mr-1.5 h-4 w-4" /> Mon entête (marque blanche)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Entête de mon garage</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">Ces coordonnées s'affichent sur le PDF remis à votre client (à la place de Stukjes).</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Nom du garage</Label>
            <Input value={c.name || ""} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="company-name" className="mt-1" placeholder="Garage Dupont SPRL" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Adresse</Label>
            <Input value={c.address || ""} onChange={(e) => setC({ ...c, address: e.target.value })} data-testid="company-address" className="mt-1" placeholder="Rue de l'Atelier 12" />
          </div>
          <div>
            <Label className="text-xs">Ville / CP</Label>
            <Input value={c.city || ""} onChange={(e) => setC({ ...c, city: e.target.value })} data-testid="company-city" className="mt-1" placeholder="1730 Asse" />
          </div>
          <div>
            <Label className="text-xs">Téléphone</Label>
            <Input value={c.phone || ""} onChange={(e) => setC({ ...c, phone: e.target.value })} data-testid="company-phone" className="mt-1" placeholder="02 123 45 67" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={c.email || ""} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="company-email" className="mt-1" placeholder="contact@garage.be" />
          </div>
          <div>
            <Label className="text-xs">N° TVA</Label>
            <Input value={c.vat || ""} onChange={(e) => setC({ ...c, vat: e.target.value })} data-testid="company-vat" className="mt-1" placeholder="BE0123.456.789" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Logo (optionnel)</Label>
            <div className="mt-1 flex items-center gap-3">
              {c.logo ? <img src={c.logo} alt="logo" className="h-12 w-20 rounded border border-slate-200 object-contain" /> : null}
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                <Upload className="h-4 w-4" /> {c.logo ? "Changer" : "Téléverser"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="company-logo-input" />
              </label>
              {c.logo && <button onClick={() => setC({ ...c, logo: "" })} className="text-xs text-rose-600">Retirer</button>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-company-btn">
            <Save className="mr-1.5 h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarginPanel({ quote, onUpdate }) {
  const lines = quote.quote?.lines || [];
  const fallbackTotal = quote.quote?.total ?? quote.quote?.price ?? 0;
  const baseLines = lines.length > 0
    ? lines
    : [{ name: quote.parts_description || "Pièces", quantity: 1, price: fallbackTotal, reference: "" }];

  const [defaultPct, setDefaultPct] = useState(String(quote.margin?.default_pct ?? 20));
  const [linePcts, setLinePcts] = useState(
    baseLines.map((_, i) => {
      const v = quote.margin?.lines?.[i];
      return v === null || v === undefined ? "" : String(v);
    })
  );
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const { i18n } = useTranslation();
  const [pdfLang, setPdfLang] = useState(i18n.language || "fr");

  const dp = parseFloat(defaultPct) || 0;
  const effPct = (i) => (linePcts[i] === "" || linePcts[i] == null ? dp : parseFloat(linePcts[i]) || 0);
  const computed = baseLines.map((l, i) => {
    const cost = Number(l.price) || 0;
    const qty = Number(l.quantity) || 1;
    const pct = effPct(i);
    const sell = cost * (1 + pct / 100);
    return { ...l, cost, qty, pct, sell, costTotal: cost * qty, sellTotal: sell * qty, profit: (sell - cost) * qty };
  });
  const totalSell = computed.reduce((s, l) => s + l.sellTotal, 0);
  const totalProfit = computed.reduce((s, l) => s + l.profit, 0);

  const marginPayload = () => ({
    default_pct: dp,
    lines: linePcts.map((v) => (v === "" || v == null ? null : parseFloat(v) || 0)),
  });

  const saveMargin = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/quotes/${quote.id}/margin`, marginPayload());
      onUpdate?.(data);
      toast.success("Marge enregistrée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const download = async (variant) => {
    setBusy(variant);
    try {
      await api.put(`/quotes/${quote.id}/margin`, marginPayload());
      const res = await api.get(`/quotes/${quote.id}/pdf?variant=${variant}&lang=${pdfLang}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `devis-${quote.id.slice(-6)}-${variant === "internal" ? "interne" : "client"}-${pdfLang}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Échec de la génération du PDF."); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-white p-6" data-testid="margin-panel">
      <div className="flex items-center gap-2 text-slate-800">
        <TrendingUp className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-base font-semibold">Marge & devis client</h2>
      </div>
      <p className="mt-1 text-xs text-slate-500">Appliquez votre marge puis imprimez un devis pour votre client — sans afficher votre prix d'achat.</p>

      {/* Global margin */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-blue-50 px-3.5 py-3">
        <div>
          <Label className="text-xs font-medium text-blue-900">Marge globale</Label>
          <p className="text-[11px] text-blue-700/70">Appliquée aux lignes sans marge spécifique.</p>
        </div>
        <div className="relative w-24">
          <Input type="number" step="1" min="0" value={defaultPct} onChange={(e) => setDefaultPct(e.target.value)}
            data-testid="margin-default-input" className="h-9 pr-6 text-right font-semibold" />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
      </div>

      {/* Per-line */}
      <div className="mt-3 space-y-2">
        {computed.map((l, i) => (
          <div key={i} className="rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`margin-line-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-slate-800">{l.qty}× {l.name}</p>
              <div className="relative w-20 shrink-0">
                <Input type="number" step="1" min="0" value={linePcts[i]}
                  onChange={(e) => setLinePcts((p) => p.map((x, idx) => (idx === i ? e.target.value : x)))}
                  placeholder={`${dp}`} data-testid={`margin-line-input-${i}`}
                  className="h-8 pr-5 text-right text-sm" />
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
              </div>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1 text-slate-400"><Lock className="h-3 w-3" /> Achat {eur(l.cost)}</span>
              <span className="text-blue-700">Vente <b>{eur(l.sell)}</b></span>
              <span className="text-emerald-600">+{eur(l.profit)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-slate-900 px-3 py-2.5 text-white">
          <p className="text-[11px] text-slate-300">Total vente client</p>
          <p className="font-display text-lg font-bold" data-testid="margin-total-sell">{eur(totalSell)}</p>
        </div>
        <div className="rounded-lg bg-emerald-600 px-3 py-2.5 text-white">
          <p className="text-[11px] text-emerald-100">Votre bénéfice</p>
          <p className="font-display text-lg font-bold" data-testid="margin-total-profit">{eur(totalProfit)}</p>
        </div>
      </div>

      <Button onClick={saveMargin} disabled={saving} variant="outline" className="mt-3 w-full border-slate-300" data-testid="save-margin-btn">
        <Save className="mr-1.5 h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer la marge"}
      </Button>

      <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          <Globe className="h-3.5 w-3.5 text-blue-600" /> Langue du PDF
          <select value={pdfLang} onChange={(e) => setPdfLang(e.target.value)} data-testid="pdf-lang-select"
            className="ml-auto rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
          </select>
        </label>
        <Button onClick={() => download("client")} disabled={!!busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="download-client-pdf">
          <FileDown className="mr-1.5 h-4 w-4" /> {busy === "client" ? "Génération..." : "PDF client (prix de vente)"}
        </Button>
        <Button onClick={() => download("internal")} disabled={!!busy} variant="outline" className="w-full border-slate-300 text-slate-700" data-testid="download-internal-pdf">
          <Sparkles className="mr-1.5 h-4 w-4" /> {busy === "internal" ? "Génération..." : "PDF interne (marge & bénéfice)"}
        </Button>
        <CompanyDialog />
      </div>
    </div>
  );
}
